import { PAGE_SEPARATOR } from '@/features/annotation/constants.ts'
import {
  PdfPageProxyLike,
  PdfTextItemLike,
  PdfViewportLike,
} from '@/features/annotation/types.ts'

import * as pdfCoordinateUtils from './pdfCoordinateUtils'

export type WordBox = {
  pageIndex: number
  localIndex: number
  globalIndex: number
  text: string
  rectPdf: { x: number; y: number; w: number; h: number }
  rectVp: { x: number; y: number; w: number; h: number }
}

export type PageRuntime = {
  pageIndex: number
  pageProxy: PdfPageProxyLike
  viewport: PdfViewportLike
  pageWidthPx: number
  heightPx: number
  wrapperEl: HTMLDivElement | null
  highlightCanvas: HTMLCanvasElement | null
  controlCanvas: HTMLCanvasElement | null
  bubbleLayerEl: HTMLDivElement | null
  words: Omit<WordBox, 'globalIndex'>[]
  pageText: string
  wordOffsets: number[]
}

export class PdfTextIndexer {
  static async buildPageWordBoxes(args: {
    pageProxy: PdfPageProxyLike
    viewport: PdfViewportLike
    pageIndex: number
  }): Promise<{
    words: Omit<WordBox, 'globalIndex'>[]
    pageText: string
    wordOffsets: number[]
  }> {
    const { pageProxy, viewport, pageIndex } = args

    const textContent = await pageProxy.getTextContent()
    const rawWords: Omit<WordBox, 'globalIndex'>[] = []

    for (const item of textContent.items) {
      if (!this.isPdfTextItemLike(item)) continue

      const tokens = this.extractTokens(item.str)
      if (tokens.length === 0) continue

      const pdfX = item.transform[4]
      const pdfY = item.transform[5]
      const totalChars = this.computeTotalChars(tokens)

      this.addTokenWordBoxes({
        pageIndex,
        viewport,
        rawWords,
        pdfX,
        pdfY,
        tokens,
        totalChars,
        itemWidth: item.width,
        itemHeight: item.height,
      })
    }

    this.sortWordsInReadingOrder(rawWords)
    this.finalizeLocalIndexes(rawWords)

    const { pageText, wordOffsets } = this.buildPageTextAndOffsets(rawWords)
    return { words: rawWords, pageText, wordOffsets }
  }

  private static extractTokens(text: string): string[] {
    // keep your current behavior; simplest tokenization
    return text.split(/\s+/).filter((t) => t.length > 0)
  }

  private static computeTotalChars(tokens: string[]): number {
    return tokens.reduce((sum, t) => sum + t.length, 0)
  }

  private static addTokenWordBoxes(args: {
    pageIndex: number
    viewport: PdfViewportLike
    rawWords: Omit<WordBox, 'globalIndex'>[]
    pdfX: number
    pdfY: number
    tokens: string[]
    totalChars: number
    itemWidth: number
    itemHeight: number
  }): void {
    const {
      pageIndex,
      viewport,
      rawWords,
      pdfX,
      pdfY,
      tokens,
      totalChars,
      itemWidth,
      itemHeight,
    } = args

    let currentX = pdfX

    for (const token of tokens) {
      const tokenWidth = itemWidth * (token.length / totalChars)

      const rectPdf = { x: currentX, y: pdfY, w: tokenWidth, h: itemHeight }
      const rectVp = pdfCoordinateUtils.rectToViewportRect(viewport, rectPdf)

      if (rectVp.w > 1 && rectVp.h > 1) {
        rawWords.push({
          pageIndex,
          localIndex: rawWords.length, // temporary; normalized later
          text: token,
          rectPdf,
          rectVp,
        })
      }

      currentX += tokenWidth
    }
  }

  private static sortWordsInReadingOrder(
    words: Omit<WordBox, 'globalIndex'>[]
  ): void {
    words.sort((a, b) => {
      const sameLine = Math.abs(a.rectVp.y - b.rectVp.y) < 5
      return sameLine ? a.rectVp.x - b.rectVp.x : a.rectVp.y - b.rectVp.y
    })
  }

  private static finalizeLocalIndexes(
    words: Omit<WordBox, 'globalIndex'>[]
  ): void {
    for (let i = 0; i < words.length; i++) {
      words[i].localIndex = i
    }
  }

  private static buildPageTextAndOffsets(
    words: Omit<WordBox, 'globalIndex'>[]
  ): {
    pageText: string
    wordOffsets: number[]
  } {
    const wordOffsets: number[] = []
    let pageText = ''

    for (let i = 0; i < words.length; i++) {
      wordOffsets.push(pageText.length)
      pageText += (i === 0 ? '' : ' ') + words[i].text
    }

    return { pageText, wordOffsets }
  }

  static buildGlobalTextIndex(pageRuntimes: Map<number, PageRuntime>): {
    text: string
    globalWordOffsets: number[]
    globalWords: WordBox[]
    wordKeyToGlobalIndex: Map<string, number>
  } {
    const sortedPageIndices = Array.from(pageRuntimes.keys()).sort(
      (a, b) => a - b
    )

    const globalWords: WordBox[] = []
    const wordKeyToGlobalIndex = new Map<string, number>()
    let globalText = ''
    const globalWordOffsets: number[] = []
    let cumulativeOffset = 0

    for (const pageIndex of sortedPageIndices) {
      const runtime = pageRuntimes.get(pageIndex)
      if (!runtime) continue

      if (globalText.length > 0) {
        globalText += PAGE_SEPARATOR
        cumulativeOffset += PAGE_SEPARATOR.length // 1
      }

      const pageStartOffset = cumulativeOffset

      for (
        let localIndex = 0;
        localIndex < runtime.words.length;
        localIndex++
      ) {
        const word = runtime.words[localIndex]
        const globalIndex = globalWords.length

        const wordBox: WordBox = {
          ...word,
          globalIndex,
        }

        globalWords.push(wordBox)

        const key = `${pageIndex}:${localIndex}`
        wordKeyToGlobalIndex.set(key, globalIndex)

        const offsetInPage = runtime.wordOffsets[localIndex] || 0
        globalWordOffsets.push(pageStartOffset + offsetInPage)
      }

      globalText += runtime.pageText
      cumulativeOffset += runtime.pageText.length
    }

    return {
      text: globalText,
      globalWordOffsets,
      globalWords,
      wordKeyToGlobalIndex,
    }
  }

  private static isPdfTextItemLike(item: unknown): item is PdfTextItemLike {
    if (!item || typeof item !== 'object') return false

    const it = item as Record<string, unknown>

    return (
      typeof it.str === 'string' &&
      Array.isArray(it.transform) &&
      it.transform.length >= 6 &&
      typeof it.width === 'number' &&
      typeof it.height === 'number'
    )
  }
}
